import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { StatusTag } from './StatusTag';
import { CLIENT_STAGE_ORDER, deliverableStage, deliverableTypeIcon, metaLabel, stageHeading } from '../utils/deliverables';
import { toDriveThumbnailUrl } from '../utils/drive';
import type { Deliverable } from '../types/portal';
function isOpenable(item: Deliverable): boolean {
  /* A transcript always opens: the page itself reads the stored transcript and
     says plainly whether one has been added yet, rather than depending on what
     this browser happens to hold. */
  if (item.type === 'Transcript') return true;
  const stage = deliverableStage(item.status);
  return stage === 'Review' || stage === 'Final Delivery';
}

/** The light that falls across every poster frame, large or small. */
export const SHEEN = 'radial-gradient(120% 140% at 20% 0%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0) 75%)';

/**
 * The small ink-and-light tile itself, shared by anything that wants the
 * same poster-frame language — a deliverable's type (below), or an episode
 * on the Show landing page. Hidden below sm: at that width it would crowd
 * out the title, which matters more.
 */
export function PosterTile({
  children
}: {
  children: React.ReactNode;
}) {
  return <span aria-hidden className="relative hidden h-9 w-14 shrink-0 items-center justify-center overflow-hidden bg-ink sm:flex" data-id="element-367" data-mp="1nobjkatekg2g">
      <span className="absolute inset-0" style={{
      background: SHEEN
    }} data-id="element-368" data-mp="2gae7nuostdol" />
      {children}
    </span>;
}

/**
 * What goes inside a poster frame at any size: the type's icon, with a real
 * frame from Drive fading in on top of it once — and only once — it has
 * loaded. A folder link, a file Drive cannot preview, or a blocked
 * thumbnail request all quietly leave the icon showing rather than flashing
 * a broken image.
 *
 * `size` is what to ASK DRIVE FOR, not what to draw. Drive returns the
 * frame scaled to fit that width, and an `<img>` then stretches whatever it
 * got to whatever the CSS says — so a source that is smaller than the box
 * is simply upscaled, quietly, with no error anywhere. **Ask for roughly
 * twice the widest CSS width the frame is ever drawn at**, because these
 * are read on retina screens. 27 Sep 2026: both callers used the 200px
 * default, which is right for the 56px list chip and was two to four times
 * too small for the film posters on the wrap-up — they were being blown up
 * from 200px to as much as 474 CSS px, which on a phone is 948 device
 * pixels of a 200-pixel picture. Drive will serve 800.
 */
function PosterContent({
  type,
  driveUrl,
  iconClass,
  size = 200
}: {
  type: Deliverable['type'];
  driveUrl?: string;
  iconClass: string;
  size?: number;
}) {
  const Icon = deliverableTypeIcon(type);
  const [loaded, setLoaded] = React.useState(false);
  const thumbnailUrl = driveUrl ? toDriveThumbnailUrl(driveUrl, size) : '';
  return <>
      <Icon className={`relative text-paper/70 ${iconClass}`} strokeWidth={1.25} data-id="element-369" data-mp="2d6c4dap5e8rk" />
      {thumbnailUrl && <img key={thumbnailUrl} src={thumbnailUrl} alt="" loading="lazy" onLoad={() => setLoaded(true)} onError={() => setLoaded(false)} className={['absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-out', loaded ? 'opacity-100' : 'opacity-0'].join(' ')} data-id="element-370" data-mp="296jyx6v1wiqf" />}
    </>;
}

/**
 * The list-row chip. Exported so the client Files list
 * (`components/FileList.tsx`) shows the exact same mark — icon or real
 * frame — for the exact same deliverable, rather than a second, plainer one.
 *
 * 56×36 CSS, so the 200px default is already more than twice what it draws.
 */
export function TypeMark({
  type,
  driveUrl
}: {
  type: Deliverable['type'];
  driveUrl?: string;
}) {
  return <PosterTile data-id="element-371" data-mp="16khyrg411cmo">
      <PosterContent type={type} driveUrl={driveUrl} iconClass="h-3.5 w-3.5" data-id="element-372" data-mp="16l1lda4mgp71" />
    </PosterTile>;
}

/**
 * A film in the index, shown as the shape it actually is.
 *
 * A REEL IS SHOWN AS A REEL. Tom: *"Vertical videos need to be viewed in
 * vertical frame, not in 16:9."* A 9:16 film letterboxed into a landscape
 * box is the studio telling its own client the wrong thing about their own
 * deliverable.
 *
 * THE EQUALISING SLOT ONLY EXISTS WHERE IT IS NEEDED. Side by side from sm
 * up, each poster sits centred inside a 16:9 slot and is sized by `h-full`,
 * so a vertical tile and a landscape one occupy rows of identical height —
 * three earlier attempts (centred in 4:3, bottom-aligned in 4:3) left
 * either three heights or three ragged tops. Stacked one per row on a
 * phone there is nothing to line up with, so the slot goes and each poster
 * takes the full width at its own aspect rather than shrinking to a stamp
 * in the middle of an empty card.
 *
 * THE FRAME IS WHATEVER DRIVE PICKS. There is no poster-frame chooser: the
 * picture is Drive's own thumbnail for that file, which is Drive's choice
 * of moment and not ours, and it can take a few minutes to exist at all
 * after an upload (until then the type icon shows instead). If the studio
 * ever needs to choose the frame, that is a stored poster image per
 * deliverable, not a bigger thumbnail request.
 */
export function FilmPoster({
  item,
  active = false
}: {
  item: Deliverable;
  active?: boolean;
}) {
  const vertical = item.videoFormat === '9:16 Vertical';
  return <span aria-hidden className="flex w-full items-center justify-center sm:aspect-video" data-id="element-373" data-mp="3cdgr3kfbsgeo">
      <span className={['relative flex w-full items-center justify-center overflow-hidden bg-ink sm:h-full sm:w-auto', vertical ? 'aspect-[9/16]' : 'aspect-video', active ? 'outline outline-1 outline-offset-4 outline-ink' : ''].join(' ')} data-id="element-374" data-mp="37g74ecjbmiuj">
        <span className="absolute inset-0" style={{
        background: SHEEN
      }} data-id="element-375" data-mp="2tgfjrsyau47k" />
        <PosterContent type={item.type} driveUrl={item.driveUrl} iconClass="h-5 w-5" size={800} data-id="element-376" data-mp="2iuj0537fcu6f" />
      </span>
    </span>;
}

/**
 * Every deliverable Portal has marked visible, grouped by the stage it is in —
 * planned work included, without actions it does not yet have.
 */
export function DeliverableGroups() {
  const {
    clientDeliverables,
    openDeliverable
  } = usePortal();
  if (clientDeliverables.length === 0) {
    return <div className="border-t border-rule py-14" data-id="element-377" data-mp="32jjiu9npk7pc">
        <span className="label block text-mute" data-id="element-378" data-mp="36jtyyf57sehx">In Motion</span>
        <p className="mt-5 max-w-sm font-display text-2xl leading-snug text-graphite" data-id="element-379" data-mp="36j24x8r9motr">
          Deliverables are being confirmed.
        </p>
        <p className="label-sm mt-4 text-mute" data-id="element-380" data-mp="2xra0beoxa9x3">
          They&rsquo;ll appear here the moment Portal adds them.
        </p>
      </div>;
  }
  const groups = CLIENT_STAGE_ORDER.map(stage => ({
    stage,
    items: clientDeliverables.filter(item => deliverableStage(item.status) === stage)
  })).filter(group => group.items.length > 0);
  return <div data-id="element-381" data-mp="3ffk6nvzcom5">
      {groups.map((group, groupIndex) => <section key={group.stage} className={groupIndex === 0 ? '' : 'mt-12'} data-id="element-382" data-mp="3vlufi38k54m3" data-mpi={groupIndex}>
          <h3 className="label border-b border-rule pb-3 text-mute" data-id="element-383" data-mp="d6s8x6mheyny">
            {stageHeading(group.stage)}
          </h3>
          <ul className="m-0 list-none p-0" data-id="element-384" data-mp="8906vjs4hw61">
            {group.items.map((item, index) => {
          const openable = isOpenable(item);
          return <li key={item.id} className="border-b border-rule" data-id="element-385" data-mp="3cmgi1bbmdbqu" data-mpi={index}>
                  <button type="button" disabled={!openable} onClick={() => openDeliverable(item.id)} className={['group grid w-full grid-cols-[3.5rem_1fr] items-baseline gap-x-5 gap-y-4 py-7 text-left sm:grid-cols-[3.25rem_4.5rem_1fr_auto] sm:gap-x-6', openable ? 'cursor-pointer' : 'cursor-default'].join(' ')} data-id="element-386" data-mp="3co9reydmaa2j">
                    <TypeMark type={item.type} driveUrl={item.driveUrl} data-id="element-387" data-mp="2vefldn8zzgx7" />
                    <span className="font-display text-3xl leading-none text-mute sm:text-4xl" data-id="element-388" data-mp="2zdobmi4twwo0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0" data-id="element-389" data-mp="394ssxohz8tm0">
                      <span className={['block font-display text-2xl leading-tight transition-colors duration-150 ease-out sm:text-4xl', openable ? 'text-ink group-hover:text-graphite' : 'text-graphite'].join(' ')} data-id="element-390" data-mp="395kdm4a1d6xi">
                        {item.name}
                      </span>
                      <span className="label-sm mt-3 block text-mute" data-id="element-391" data-mp="3s2c2ct8cke8y">{metaLabel(item)}</span>
                    </span>
                    <span className="col-start-2 flex items-center gap-4 sm:col-start-4" data-id="element-392" data-mp="1ssdnk35g4mwm">
                      <StatusTag status={item.status} data-id="element-393" data-mp="1xqxeyj2t6qwd" />
                      {openable && <ArrowRightIcon className="hidden h-3.5 w-3.5 text-mute transition-transform duration-150 ease-out group-hover:translate-x-1 sm:block" strokeWidth={1.25} data-id="element-394" data-mp="1q7o56oqe4q6s" />}
                    </span>
                  </button>
                </li>;
        })}
          </ul>
        </section>)}
    </div>;
}